//
// # useConstraints Hook
//
// Watches the active editor and extracts constraints from the content.
//
// - For schematics: reads constraints directly from the editor's in-memory content.
// - For layouts: follows the schematicImpl reference and fetches that
//   schematic's constraints from the server.
// - For symbols or empty state: returns null.
//
// Re-fetches whenever the active ident changes or content is reloaded.
//

import { useEffect, useState } from "react";
import { logger } from "CoreUtils";
import type { schema } from "GenAlphaSchema";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { createRequestContext } from "../../contextHelpers";

interface ConstraintsState {
  readonly constraints: schema.schematic.Constraints | null;
  readonly loading: boolean;
  readonly viewKind: "schematic" | "layout" | null;
}

export function useConstraints(): ConstraintsState {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const activeIdent = snapshot.activeIdent;
  const contentVersion = snapshot.contentVersion;
  const constraintsVersion = snapshot.constraintsVersion;

  const [state, setState] = useState<ConstraintsState>({
    constraints: null,
    loading: false,
    viewKind: null,
  });

  // Determine the current view kind to decide which version counter to use.
  const editor = workspace.getActiveEditor();
  const currentViewKind = editor?.content.contentKind;

  // For schematics: re-fetch on contentVersion (constraints are in the content).
  // For layouts: re-fetch on constraintsVersion only (layout edits like routing
  // don't affect constraints, avoiding panel flicker).
  const effectTrigger =
    currentViewKind === "schematic" ? contentVersion : constraintsVersion;

  useEffect(() => {
    if (!activeIdent) {
      setState({ constraints: null, loading: false, viewKind: null });
      return;
    }

    let cancelled = false;

    const fetchConstraints = async () => {
      const editor = workspace.getActiveEditor();
      if (!editor) {
        setState({ constraints: null, loading: false, viewKind: null });
        return;
      }

      const content = editor.content;
      if (content.contentKind === "schematic") {
        const schematic = content as {
          constraints?: schema.schematic.Constraints;
        };
        const constraints = schematic.constraints ?? null;
        setState({ constraints, loading: false, viewKind: "schematic" });
        return;
      }

      if (content.contentKind === "layout") {
        const layoutContent = content as { schematicImpl?: schema.Ident };
        const schematicImpl = layoutContent.schematicImpl;
        if (!schematicImpl) {
          setState({ constraints: null, loading: false, viewKind: "layout" });
          return;
        }

        setState((prev) => ({ ...prev, loading: true }));

        const ctx = createRequestContext(workspace.userId);
        const schResult = await workspace.client.request(ctx, {
          kind: "open",
          data: { kind: "ident", ident: schematicImpl },
        });

        if (cancelled) return;

        if (schResult.err) {
          logger.error(
            `[useConstraints] Failed to fetch schematic for layout: ${schResult.val.why}`
          );
          setState({ constraints: null, loading: false, viewKind: "layout" });
          return;
        }

        const schData = schResult.val as Record<string, unknown>;
        const constraints =
          (schData.constraints as schema.schematic.Constraints | undefined) ??
          null;
        setState({ constraints, loading: false, viewKind: "layout" });
        return;
      }

      // Unsupported content kind
      setState({ constraints: null, loading: false, viewKind: null });
    };

    void fetchConstraints();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIdent, effectTrigger, workspace]);

  // Derive viewKind synchronously from the editor so buttons are visible
  // immediately, without waiting for the async constraint fetch.
  const viewKind: ConstraintsState["viewKind"] =
    currentViewKind === "schematic"
      ? "schematic"
      : currentViewKind === "layout"
      ? "layout"
      : null;

  return { ...state, viewKind };
}
